import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import React, { Suspense, lazy, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getToken, hasPermission } from "./server";
import SiteLayout from "./site/SiteLayout";
import Home from "./site/pages/Home";

// Website pages other than Home load on demand.
const About = lazy(() => import("./site/pages/About"));
const Treatments = lazy(() => import("./site/pages/Treatments"));
const Book = lazy(() => import("./site/pages/Book"));
const Contact = lazy(() => import("./site/pages/Contact"));
const MediaGallery = lazy(() => import("./site/pages/MediaGallery"));
const PrivacyPolicy = lazy(() => import("./site/pages/PrivacyPolicy"));
const StaffAttendance = lazy(() => import("./site/pages/StaffAttendance"));
const NotFound = lazy(() => import("./site/pages/NotFound"));

// Old software links (before it moved under /manage) still work.
const LEGACY = [
  "dashboard", "appointments", "sessions", "session-charges", "clients", "treatment-plans",
  "exercise-library", "prescriptions", "progress-photos", "feedback", "employees", "attendance",
  "payroll", "expenses", "vendors", "payment-reminders", "financial-reports", "equipment",
  "retention-analysis", "campaigns", "users-roles",
];

const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Appointments = lazy(() => import("./pages/Appointments"));
const SessionTracking = lazy(() => import("./pages/SessionTracking"));
const SessionCharges = lazy(() => import("./pages/SessionCharges"));
const Clients = lazy(() => import("./pages/Clients"));
const TreatmentPlans = lazy(() => import("./pages/TreatmentPlans"));
const ExerciseLibrary = lazy(() => import("./pages/ExerciseLibrary"));
const Prescriptions = lazy(() => import("./pages/Prescriptions"));
const ProgressPhotos = lazy(() => import("./pages/ProgressPhotos"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Employees = lazy(() => import("./pages/Employees"));
const Expenses = lazy(() => import("./pages/Expenses"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Equipment = lazy(() => import("./pages/Equipment"));
const PaymentReminders = lazy(() => import("./pages/PaymentReminders"));
const FinancialReports = lazy(() => import("./pages/FinancialReports"));
const RetentionAnalysis = lazy(() => import("./pages/RetentionAnalysis"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const UsersRoles = lazy(() => import("./pages/UsersRoles"));
const MediaGalleryAdmin = lazy(() => import("./pages/MediaGalleryAdmin"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  // The clinic software keeps its previous zoomed-out layout on phones;
  // the public website uses normal 1:1 scaling.
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]');
    if (vp) {
      vp.setAttribute(
        "content",
        pathname.startsWith("/manage") ? "width=device-width, initial-scale=0.8" : "width=device-width, initial-scale=1"
      );
    }
  }, [pathname]);
  return null;
}

function PrivateRoute({ perm, children }) {
  if (!getToken()) return <Navigate to="/manage" replace />;
  if (perm && !hasPermission(perm)) return <Navigate to="/manage/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
      <Routes>
        {/* ── Staff attendance (opened from the QR code at the clinic) ── */}
        <Route path="/attendance" element={<StaffAttendance />} />

        {/* ── Public website ── */}
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/treatments" element={<Treatments />} />
          <Route path="/book" element={<Book />} />
          <Route path="/media-gallery" element={<MediaGallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* ── Clinic software (staff only) ── */}
        <Route path="/manage" element={<Login />} />
        <Route path="/manage/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />

        <Route path="/manage/appointments" element={<PrivateRoute perm="Appointments"><Appointments /></PrivateRoute>} />

        <Route path="/manage/sessions" element={<PrivateRoute perm="Sessions"><SessionTracking /></PrivateRoute>} />
        <Route path="/manage/session-charges" element={<PrivateRoute perm="Sessions"><SessionCharges /></PrivateRoute>} />

        <Route path="/manage/clients" element={<PrivateRoute perm="Clients"><Clients /></PrivateRoute>} />
        <Route path="/manage/treatment-plans" element={<PrivateRoute perm="Clients"><TreatmentPlans /></PrivateRoute>} />
        <Route path="/manage/exercise-library" element={<PrivateRoute perm="Clients"><ExerciseLibrary /></PrivateRoute>} />
        <Route path="/manage/prescriptions" element={<PrivateRoute perm="Clients"><Prescriptions /></PrivateRoute>} />
        <Route path="/manage/progress-photos" element={<PrivateRoute perm="Clients"><ProgressPhotos /></PrivateRoute>} />
        <Route path="/manage/feedback" element={<PrivateRoute perm="Clients"><Feedback /></PrivateRoute>} />

        <Route path="/manage/employees" element={<PrivateRoute perm="Employee"><Employees defaultTab="profiles" /></PrivateRoute>} />
        <Route path="/manage/attendance" element={<PrivateRoute perm="Employee"><Employees defaultTab="attendance" /></PrivateRoute>} />
        <Route path="/manage/payroll" element={<PrivateRoute perm="Employee"><Employees defaultTab="payroll" /></PrivateRoute>} />

        <Route path="/manage/expenses" element={<PrivateRoute perm="Finance"><Expenses /></PrivateRoute>} />
        <Route path="/manage/vendors" element={<PrivateRoute perm="Finance"><Vendors /></PrivateRoute>} />
        <Route path="/manage/payment-reminders" element={<PrivateRoute perm="Finance"><PaymentReminders /></PrivateRoute>} />
        <Route path="/manage/financial-reports" element={<PrivateRoute perm="Finance"><FinancialReports /></PrivateRoute>} />

        <Route path="/manage/equipment" element={<PrivateRoute perm="Equipments"><Equipment /></PrivateRoute>} />

        <Route path="/manage/retention-analysis" element={<PrivateRoute perm="Analytics"><RetentionAnalysis /></PrivateRoute>} />

        <Route path="/manage/campaigns" element={<PrivateRoute perm="Communication"><Campaigns /></PrivateRoute>} />
        <Route path="/manage/media-gallery" element={<PrivateRoute perm="Communication"><MediaGalleryAdmin /></PrivateRoute>} />

        <Route path="/manage/users-roles" element={<PrivateRoute><UsersRoles /></PrivateRoute>} />

        <Route path="/manage/*" element={<Navigate to="/manage" replace />} />
        {LEGACY.map((p) => (
          <Route key={p} path={`/${p}`} element={<Navigate to={`/manage/${p}`} replace />} />
        ))}
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
